import { useIonRouter, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import SequenceContent from "../../components/organisms/SequenceDetails/SequenceContent";
import SequenceQuestionnaire from "../../components/organisms/SequenceDetails/SequenceQuestionnaire";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useFetch } from "../../hooks/useFetch";
import {
  FullSequenceResponse,
  getApiFullSequence,
} from "../../services/questionnaire.service";
import { setSequenceComponentDone } from "../../services/sequence.service";
import { ComponentType } from "../../types/Questionnaire";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import { useRef } from 'react';

export const sequenceIsDone = (components: ComponentType[]): boolean => {
  return components.every((c) => c.is_done);
};

const SequenceComponent: FC<RouteComponentProps> = ({ history }) => {
  const { componentId, sequenceId, title, study_slug } = useParams<{
    componentId: string;
    sequenceId: string;
    study_slug: string;
    title: string;
  }>();

  const { setResonanceLayout } = useResonanceLayout();
  const { user } = useAuth();
  const { study } = useConfig();
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();

  const ionRouter = useIonRouter();
  const { updateScore, updateSequenceComponentDone, updateSequenceDone } =
    useResonanceData();

  useEffect(() => {
    console.log(
      "[SequenceComponent] mounted with params:",
      sequenceId,
      componentId
    );
  }, [sequenceId, componentId]);

  const { data, isLoading, isError } = useFetch<FullSequenceResponse>(
    `full-sequence:${sequenceId}`,
    getApiFullSequence,
    {
      studyId: study.id,
      userId: user.id,
      sequenceId: parseInt(sequenceId),
      lang: language,
    }
  );

  const sequence = data?.sequence;
  const answers = data?.answers?.answers ?? [];
  const component = useMemo(
    () => sequence?.components.find((c) => c.id === parseInt(componentId)),
    [sequence, componentId]
  );

  const questionnaire = useMemo(
    () =>
      data?.questionnaires?.find(
        (q) => String(q.id) === String(component?.questionnaire_id)
      ),
    [data?.questionnaires, component?.questionnaire_id]
  );

  const dependencies = useMemo(
    () =>
      questionnaire && data?.dependencies
        ? data.dependencies[questionnaire.id] ?? []
        : [],
    [questionnaire, data?.dependencies]
  );

  const qAnswers = useMemo(
    () =>
      answers.filter((a) => a.questionnaire_id === component?.questionnaire_id),
    [answers, component?.questionnaire_id]
  );

    useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: true,
      backgroundColor: "white",
      welcomePage: false,
    });
  });

  console.log("[SequenceComponent] full sequence data:", data);
  console.log("[SequenceComponent] looking for componentId:", componentId);
  console.log("[SequenceComponent] resolved component:", component);
  console.log("[SequenceComponent] resolved questionnaire:", questionnaire);

  useEffect(() => {
    console.log("[SequenceComponent] useEffect data changed", {
      data,
      isLoading,
      isError,
    });
  }, [data, isLoading, isError]);

  const currentId = component?.id ?? Number(componentId);

// If you want an extra guard against any flicker, keep the last good id:

const currentIdRef = useRef<number | null>(null);
if (component?.id && currentIdRef.current !== component.id) {
  currentIdRef.current = component.id;
}
const stableCurrentId = currentIdRef.current ?? currentId;

// Now compute index strictly from the resolved component, not params:
const componentIndex =
  sequence?.components.findIndex((c) => c.id === stableCurrentId) ?? -1;

// Find the next one (simple “next-in-order”):
const nextComponent =
  componentIndex >= 0 ? sequence!.components[componentIndex + 1] ?? null : null;

// If you prefer “skip done ones”:
const nextUndone =
  componentIndex >= 0
    ? sequence!.components.slice(componentIndex + 1).find((c) => !c.is_done) ?? null
    : null;

  /** ✅ When all components of a sequence are done */
  const handleDone = () => {
    if (title) {
    history.replace(`/${study_slug}/tabs/home/island/${title}`);

    } else {
      history.replace(`/${study_slug}/tabs/questionnaires`);
    }
  };

  /** ✅ Go to the next component */
  const handleNext = () => {
    if (nextComponent) {
      if (title) {
        // From home island
        ionRouter.push(
          `/${study_slug}/tabs/home/island/${title}/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      } else {
        // From questionnaires tab
        ionRouter.push(
          `/${study_slug}/tabs/questionnaires/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      }
    } else {
      handleDone();
    }
  };
  // const handleSequnceDone = () => {
  //   if (isLastSequence) {
  //     if (
  //       selectedRole.signin_criteria.method ===
  //       IdentificationMethodEnum.WITHOUT_IDENTIFICATION
  //     ) {
  //       endSession();
  //       history.replace(`/${study_slug}`);
  //     } else {
  //       history.replace(`/${study_slug}/home`);
  //     }
  //   } else {
  //     history.push(`/${study_slug}/sequences`);
  //   }
  // };

  // if (showSequenceDone)
  //   return <FinishedQuestionnaires onClick={handleSequnceDone} />;
  if (isLoading || !data) {
    return <ResonanceLoader />;
  }

  if (isError || !sequence) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }
  if (!component || !questionnaire) {
    return <ResonanceLoader />;
  }

  return (
    //this one is for showing the questionnaires indirectly
    // <SequenceContent
    //   studyId={study.id}
    //   contentId={component.content_id}
    //   sequenceId={parseInt(sequenceId)}
    //   onStartClick={handleNext}
    //   onPrevClick={() =>
    //     router.canGoBack() ? router.goBack() : history.goBack()
    //   }
    // />

    //this one is for showing the questionnaires directly
    <ResonanceLayout isConnected>
    <SequenceQuestionnaire
      key={`sq-${sequenceId}-${component.id}`}
      questionnaireId={component.questionnaire_id}
      questionnaireTitle={component.questionnaire_title}
      questionnaireData={questionnaire}
      answers={qAnswers}
      dependencies={dependencies}
      componentId={component.id}
      onNextClick={handleNext}
      onPrevClick={() =>
        ionRouter.canGoBack() ? ionRouter.goBack() : history.goBack()
      }
      onQuestionnaireDone={async (totalScore) => {
        const { data } = await setSequenceComponentDone(
          {
            studyId: study.id,
            userId: user.id,
            sequenceId,
            sequenceComponentId: component.id,
          },
          totalScore
        );

        // update local scores
        if (component.is_scored && totalScore !== null) {
          updateScore(
            data.userSequenceComponentId,
            data.userSequenceId,
            data.sequenceId,
            data.sequenceComponentId,
            data.questionnaireId,
            data.score
          );
        }

        // update local sequence/component status
        updateSequenceComponentDone(
          data.userSequenceId,
          data.sequenceComponentId
        );
        if (data.allDone) {
          updateSequenceDone(data.userSequenceId);
        }
      }}
    />
    </ResonanceLayout>
  );
};

export default SequenceComponent;
